import { useEffect, useRef, useState } from 'react'
import { useParams, Navigate, Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { UnsavedChangesDialog } from '@/components/UnsavedChangesDialog'
import { SkillSectionsNav } from '@/components/projects'
import {
  useAuth,
  useCreateSkillVersion,
  useCreateSkillWithAttachments,
  useProject,
  useSkill,
  useUnsavedChangesGuard,
  useUpdateSkill,
} from '@/hooks'
import type { SkillDetailContext } from '@/hooks'
import { getErrorMessage } from '@/api/errors'
import { extractValidationMessage, getValidationErrorMessage } from '@/lib/validationErrors'
import { Permissions } from '@/lib/permissions'
import { SKILL_CONTENT_MAX } from '@/lib/skillLimits'
import { toSlug } from '@/lib/slug'
import { KEBAB_CASE_REGEX } from '@/types'
import type { SkillDetail } from '@/types'
import { toast } from 'sonner'

const NEUTRAL_BADGE_BORDER = 'rgba(118,124,150,1)'
const NEW_SKILL_ID = 'new'

export function SkillDetailPage() {
  const { skillId } = useParams()
  return <SkillDetailContent key={skillId} />
}

function SkillDetailContent() {
  const { projectId, skillId } = useParams()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const isNew = skillId === NEW_SKILL_ID
  const { hasPermission, isLoading: authLoading } = useAuth(projectId)
  const { data: skill, isLoading, isError, isFetching } = useSkill(projectId!, isNew ? undefined : skillId)
  const { data: project } = useProject(projectId)
  const updateSkill = useUpdateSkill(projectId!)
  const createVersion = useCreateSkillVersion(projectId!)
  const createSkill = useCreateSkillWithAttachments(projectId!)

  const [displayNameDraft, setDisplayNameDraft] = useState<string | null>(null)
  const [internalNameDraft, setInternalNameDraft] = useState<string | null>(null)
  const [internalNameError, setInternalNameError] = useState<string | null>(null)
  const [descriptionDraft, setDescriptionDraft] = useState<string | null>(null)
  const [contentDraft, setContentDraft] = useState<string | null>(null)
  const [pendingAttachments, setPendingAttachments] = useState<File[]>([])
  const [saveAttempted, setSaveAttempted] = useState(false)
  const saveInFlight = useRef(false)

  const savedSkill = skill ?? null
  const displayName = displayNameDraft ?? savedSkill?.displayName ?? ''
  const internalName = savedSkill?.name ?? internalNameDraft ?? toSlug(displayName)
  const description = descriptionDraft ?? savedSkill?.description ?? ''
  const content = contentDraft ?? savedSkill?.content ?? ''

  const trimmedDisplayName = displayName.trim()
  const trimmedInternalName = internalName.trim()
  const trimmedDescription = description.trim()
  const trimmedContent = content.trim()
  const metadataChanged =
    trimmedDisplayName !== (savedSkill?.displayName ?? '') ||
    trimmedDescription !== (savedSkill?.description ?? '')
  const contentChanged = trimmedContent !== (savedSkill?.content.trim() ?? '')
  const isDirty = metadataChanged || contentChanged || internalNameDraft !== null || pendingAttachments.length > 0
  const contentTooLong = content.length > SKILL_CONTENT_MAX

  const isSaving = updateSkill.isPending || createVersion.isPending || createSkill.isPending

  let internalNameProblem: string | null = null
  if (savedSkill) internalNameProblem = null
  else if (!trimmedInternalName) internalNameProblem = 'Internal name is required.'
  else if (!KEBAB_CASE_REGEX.test(trimmedInternalName)) internalNameProblem = 'Internal name must be kebab-case (e.g. "api-error-handling").'

  let validationError: string | null = null
  if (!trimmedDisplayName) validationError = 'Display name is required.'
  else if (internalNameProblem) validationError = internalNameProblem
  else if (internalNameError) validationError = internalNameError
  else if (!trimmedContent) validationError = 'Content is required.'
  else if (contentTooLong) validationError = `The content must not exceed ${SKILL_CONTENT_MAX} characters (currently ${content.length}).`

  let saveBlockedReason: string | null = null
  if (isSaving) saveBlockedReason = 'Saving…'
  else if (!isDirty) saveBlockedReason = 'No changes to save.'
  else if (isFetching) saveBlockedReason = 'Reloading the skill…'

  const skillsPath = `/projects/${projectId}/workflows/skills`
  const skillPath = `${skillsPath}/${skillId}`
  const pathnameRef = useRef(pathname)
  useEffect(() => {
    pathnameRef.current = pathname
  }, [pathname])
  const currentSection = () =>
    pathnameRef.current.startsWith(`${skillPath}/`) ? pathnameRef.current.slice(skillPath.length + 1) : 'config'

  const guard = useUnsavedChangesGuard({
    isDirty: isDirty || isSaving,
    isNavigationAllowed: next => next.pathname === skillPath || next.pathname.startsWith(`${skillPath}/`),
  })

  const discardDrafts = () => {
    setDisplayNameDraft(null)
    setInternalNameDraft(null)
    setInternalNameError(null)
    setDescriptionDraft(null)
    setContentDraft(null)
    setPendingAttachments([])
    setSaveAttempted(false)
  }

  const saveExisting = async (existing: SkillDetail): Promise<boolean> => {
    let metadataSaved = false
    try {
      if (metadataChanged) {
        await updateSkill.mutateAsync({
          id: existing.id,
          data: {
            displayName: trimmedDisplayName,
            ...(trimmedDescription ? { description: trimmedDescription } : {}),
          },
        })
        metadataSaved = true
        setDisplayNameDraft(prev => (prev === displayName ? null : prev))
        setDescriptionDraft(prev => (prev === description ? null : prev))
      }
      if (contentChanged) {
        const response = await createVersion.mutateAsync({ id: existing.id, data: { content: trimmedContent } })
        setContentDraft(prev => (prev === content ? null : prev))
        toast.success(`Saved "${trimmedDisplayName}" as version ${response.version} — older versions stay available, so a workflow can pin an exact one.`)
      } else {
        toast.success(`Saved "${trimmedDisplayName}"`)
      }
      return true
    } catch (error) {
      const reason = extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Failed to save skill'
      toast.error(metadataSaved ? `Name and description were saved, but the content wasn't: ${reason}` : reason)
      return false
    }
  }

  const createNew = async (): Promise<{ skillId: string; attachmentsFailed: boolean } | null> => {
    try {
      const created = await createSkill.mutateAsync({
        request: {
          name: trimmedInternalName,
          displayName: trimmedDisplayName,
          content: trimmedContent,
          ...(trimmedDescription ? { description: trimmedDescription } : {}),
        },
        attachments: pendingAttachments,
      })
      if (created.attachmentsError) {
        const reason = extractValidationMessage(created.attachmentsError) ?? getErrorMessage(created.attachmentsError) ?? 'upload failed'
        const fileNames = pendingAttachments.map(file => file.name).join(', ')
        toast.error(`"${trimmedDisplayName}" was created, but these attachments weren't uploaded: ${fileNames}. ${reason}`)
      } else {
        toast.success(`Created "${trimmedDisplayName}" as version ${created.version}`)
      }
      return { skillId: created.skillId, attachmentsFailed: created.attachmentsError !== null }
    } catch (error) {
      setInternalNameError(getValidationErrorMessage(error, 'Name'))
      toast.error(extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Failed to create skill')
      return null
    }
  }

  const runExclusive = async (save: () => Promise<void>) => {
    if (saveInFlight.current) return
    saveInFlight.current = true
    try {
      await save()
    } finally {
      saveInFlight.current = false
    }
  }

  const openCreatedSkill = (createdSkillId: string, section: string) => {
    guard.stay()
    guard.allowNextNavigation()
    navigate(`${skillsPath}/${createdSkillId}/${section}`, { replace: true })
  }

  const finishCreate = (created: { skillId: string; attachmentsFailed: boolean }) => {
    if (created.attachmentsFailed) openCreatedSkill(created.skillId, 'attachments')
    else if (guard.isBlockedNow()) guard.leave()
    else openCreatedSkill(created.skillId, currentSection())
  }

  const handleSave = () => runExclusive(async () => {
    setSaveAttempted(true)
    if (validationError) {
      if (currentSection() !== 'config') navigate(`${skillPath}/config`)
      return
    }
    if (savedSkill) {
      if (await saveExisting(savedSkill)) setSaveAttempted(false)
      return
    }
    const created = await createNew()
    if (created) finishCreate(created)
  })

  const handleSaveAndLeave = () => runExclusive(async () => {
    if (validationError) return
    if (savedSkill) {
      if (await saveExisting(savedSkill)) guard.leave()
      return
    }
    const created = await createNew()
    if (created) finishCreate(created)
  })

  const handleCancel = () => {
    if (savedSkill) {
      discardDrafts()
      return
    }
    if (isDirty) guard.allowNextNavigation()
    navigate(skillsPath)
  }

  if (authLoading) return null

  if (!hasPermission(isNew ? Permissions.Project.Skill.Create : Permissions.Project.Skill.Read, projectId)) {
    return <Navigate to={isNew ? skillsPath : `/projects/${projectId}/backlog`} replace />
  }

  if (!isNew && isLoading) {
    return (
      <div className="py-12 text-center text-muted-foreground" role="status" aria-live="polite" data-testid="skill-detail-loading">
        Loading skill…
      </div>
    )
  }

  if (!isNew && !savedSkill) {
    return (
      <div className="space-y-4 py-12 text-center" role="alert" data-testid="skill-detail-error">
        <p className="text-sm text-destructive">Couldn't load this skill. It may have been deleted.</p>
        <Button variant="outline" asChild>
          <Link to={skillsPath}>
            <ArrowLeft className="h-4 w-4" />
            Back to Skills
          </Link>
        </Button>
      </div>
    )
  }

  const canEdit = savedSkill ? !savedSkill.isReadOnly && hasPermission(Permissions.Project.Skill.Update, projectId) : true

  const context: SkillDetailContext = {
    projectId: projectId!,
    skill: savedSkill,
    canEdit,
    displayName,
    onDisplayNameChange: value => {
      setDisplayNameDraft(value)
      if (internalNameDraft === null) setInternalNameError(null)
    },
    internalName,
    onInternalNameChange: value => {
      setInternalNameDraft(value)
      setInternalNameError(null)
    },
    internalNameError: internalNameError ?? (saveAttempted && !savedSkill ? internalNameProblem : null),
    showValidation: saveAttempted,
    description,
    onDescriptionChange: setDescriptionDraft,
    content,
    onContentChange: setContentDraft,
    contentTooLong,
    pendingAttachments,
    onPendingAttachmentsChange: setPendingAttachments,
    isDirty,
    isSaving,
    saveBlockedReason,
    onSave: () => void handleSave(),
    onCancel: handleCancel,
  }

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col gap-6" data-testid="skill-detail-page">
      <div className="min-w-0 shrink-0">
        <p className="text-sm font-medium text-primary">
          Project: {project?.name ?? 'Loading...'}
        </p>
        <div className="flex min-w-0 items-center gap-3">
          <Button variant="ghost" size="icon" asChild className="shrink-0" aria-label="Back to Skills">
            <Link to={skillsPath}>
              <ArrowLeft className="h-5 w-5" />
            </Link>
          </Button>
          <h1 className="truncate text-2xl font-bold text-foreground">{savedSkill?.displayName ?? 'New Skill'}</h1>
          {savedSkill?.isSystem ? (
            <>
              <Badge variant="outline" className="shrink-0 text-xs" style={{ borderColor: NEUTRAL_BADGE_BORDER }} data-testid="skill-category-chip">System</Badge>
              <Badge variant="outline" className="shrink-0 text-xs text-muted-foreground" style={{ borderColor: NEUTRAL_BADGE_BORDER }} data-testid="skill-readonly-chip">Read-only</Badge>
            </>
          ) : (
            <Badge variant="outline" className="shrink-0 text-xs" style={{ borderColor: NEUTRAL_BADGE_BORDER }} data-testid="skill-category-chip">Custom</Badge>
          )}
        </div>
      </div>

      {isError && (
        <p className="shrink-0 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-2 text-sm text-destructive" role="alert" data-testid="skill-detail-stale">
          Couldn't refresh this skill — it may have been deleted. Anything you have edited here is still unsaved.
        </p>
      )}

      <div className="flex min-h-0 flex-1 gap-4">
        <SkillSectionsNav />
        <div className="min-h-0 min-w-0 flex-1">
          <Outlet context={context} />
        </div>
      </div>

      <UnsavedChangesDialog
        open={guard.isBlocked}
        isSaving={isSaving}
        saveBlockedReason={isSaving ? null : validationError ?? saveBlockedReason}
        onSave={() => void handleSaveAndLeave()}
        onDiscard={guard.leave}
        onStay={guard.stay}
      />
    </div>
  )
}
